import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Divider,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import TeamColorPicker from '@/components/review/TeamColorPicker';
import DecisionFields, { eventToDecisionForm, normalizeDecisionPayload, type DecisionFormValues } from '@/components/review/DecisionFields';
import LiveActionZones from '@/components/live/LiveActionZones';
import { DEFAULT_AWAY_COLOR, DEFAULT_HOME_COLOR } from '@/types';

export interface SessionFormValues {
  title: string;
  competition_name: string;
  home_team: string;
  away_team: string;
  match_date: string;
  youtube_url?: string;
  notes: string;
  home_team_color: string;
  away_team_color: string;
}

interface Props {
  open: boolean;
  title: string;
  initial?: Partial<SessionFormValues>;
  showYoutube?: boolean;
  onClose: () => void;
  onSubmit: (values: SessionFormValues) => void;
}

const empty: SessionFormValues = {
  title: '',
  competition_name: '',
  home_team: '',
  away_team: '',
  match_date: '',
  youtube_url: '',
  notes: '',
  home_team_color: DEFAULT_HOME_COLOR,
  away_team_color: DEFAULT_AWAY_COLOR,
};

export default function SessionFormDialog({
  open,
  title,
  initial,
  showYoutube,
  onClose,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<SessionFormValues>(empty);

  useEffect(() => {
    if (open) {
      setValues({ ...empty, ...initial });
    }
  }, [open, initial]);

  const handleChange = (field: keyof SessionFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="標題 *" value={values.title} onChange={handleChange('title')} fullWidth required />
          <TextField label="賽事名稱" value={values.competition_name} onChange={handleChange('competition_name')} fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="主隊" value={values.home_team} onChange={handleChange('home_team')} fullWidth />
            <TextField label="客隊" value={values.away_team} onChange={handleChange('away_team')} fullWidth />
          </Stack>

          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>隊伍顏色</Typography>
          <TeamColorPicker
            homeColor={values.home_team_color}
            awayColor={values.away_team_color}
            homeLabel={values.home_team || '主隊'}
            awayLabel={values.away_team || '客隊'}
            onChange={(side, color) =>
              setValues((v) => ({
                ...v,
                [side === 'home' ? 'home_team_color' : 'away_team_color']: color,
              }))
            }
          />

          <TextField
            label="比賽日期"
            type="date"
            value={values.match_date}
            onChange={handleChange('match_date')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {showYoutube && (
            <TextField
              label="YouTube 連結或影片 ID"
              value={values.youtube_url}
              onChange={handleChange('youtube_url')}
              fullWidth
              placeholder="https://www.youtube.com/watch?v=... 或 /live/..."
              helperText="支援 watch、youtu.be、shorts、live 連結，或直接貼上 11 位影片 ID"
            />
          )}
          <TextField label="備註" value={values.notes} onChange={handleChange('notes')} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" color="secondary" disabled={!values.title.trim()} onClick={() => onSubmit(values)}>
          儲存
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EventFormDialog({
  open,
  initial,
  onClose,
  onSubmit,
  showVideoTime,
  videoDuration,
  variant = 'review',
  zIndex,
  menuZIndex,
  dark,
}: {
  open: boolean;
  initial?: {
    event_type?: string;
    note?: string | null;
    team_side?: string | null;
    on_field_decision?: string | null;
    on_field_punishment?: string | null;
    reviewed_decision?: string | null;
    reviewed_punishment?: string | null;
    judgement?: string | null;
    video_second?: number | null;
  };
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
  showVideoTime?: boolean;
  videoDuration?: number;
  variant?: 'review' | 'live';
  /** Raise above fullscreen overlays (e.g. live recording mode). */
  zIndex?: number;
  /** Raise select menus above the dialog / overlay shell. */
  menuZIndex?: number;
  dark?: boolean;
}) {
  const [form, setForm] = useState<DecisionFormValues>(eventToDecisionForm());
  const [eventType, setEventType] = useState('note');
  const [note, setNote] = useState('');
  const [videoTime, setVideoTime] = useState('0:00');

  const secondsToInput = (sec?: number | null) => {
    if (sec == null || Number.isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const parseVideoTime = (input: string): number | undefined => {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    if (trimmed.includes(':')) {
      const [m, s] = trimmed.split(':');
      const mins = parseInt(m, 10);
      const secs = parseInt(s, 10);
      if (Number.isFinite(mins) && Number.isFinite(secs)) return mins * 60 + secs;
    }
    const n = parseFloat(trimmed);
    return Number.isFinite(n) ? n : undefined;
  };

  useEffect(() => {
    if (open) {
      if (variant === 'live') {
        setEventType(initial?.event_type || 'note');
        setNote(initial?.note || '');
      } else {
        setForm(eventToDecisionForm(initial));
      }
      setVideoTime(secondsToInput(initial?.video_second));
    }
  }, [open, initial, variant]);

  const handleSubmit = () => {
    if (variant === 'live') {
      onSubmit({ event_type: eventType, note, team_side: null });
      return;
    }
    const parsed = parseVideoTime(videoTime);
    const payload = normalizeDecisionPayload(form);
    if (showVideoTime && parsed != null) {
      const max = videoDuration && videoDuration > 0 ? videoDuration : parsed;
      onSubmit({ ...payload, video_second: Math.min(Math.max(0, parsed), max) });
    } else {
      onSubmit(payload);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={variant === 'live' ? 'md' : 'sm'}
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogTitle>{variant === 'live' ? '編輯事件' : '編輯判決標記'}</DialogTitle>
      <DialogContent sx={variant === 'live' ? { pb: 0 } : undefined}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {variant === 'live' ? (
            <>
              <Typography variant="subtitle2" fontWeight={700}>
                快速記錄區
              </Typography>
              <Typography variant="caption" color="text.secondary">
                點選按鈕以更改事件類型
              </Typography>
              <Box sx={{ maxHeight: '55vh', overflow: 'auto', pr: 0.5 }}>
                <LiveActionZones
                  selectedKey={eventType}
                  onActionTap={(action) => setEventType(action.key)}
                />
              </Box>
              <TextField label="備註" value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline rows={3} />
            </>
          ) : (
            <>
              {showVideoTime && (
                <TextField
                  label="影片時間 (m:ss)"
                  value={videoTime}
                  onChange={(e) => setVideoTime(e.target.value)}
                  placeholder="例：1:23"
                  helperText={videoDuration ? `影片長度 ${secondsToInput(videoDuration)}` : '可輸入 m:ss 或秒數'}
                  fullWidth
                />
              )}
              <DecisionFields
                values={form}
                onChange={(field, value) => setForm((v) => ({ ...v, [field]: value }))}
                dark={dark}
                menuZIndex={menuZIndex ?? (zIndex != null ? zIndex + 10 : undefined)}
                dialogZIndex={zIndex != null ? zIndex + 20 : undefined}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit}>
          儲存
        </Button>
      </DialogActions>
    </Dialog>
  );
}
